import { IotaClient } from '@iota/iota-sdk/client';
import {
  IdentifierString,
  IotaSignTransactionFeature,
  StandardDisconnectFeature,
  StandardEventsFeature,
} from '@iota/wallet-standard';
import { WalletWithFeatures } from '@wallet-standard/base';
import type {
  StandardConnectFeature,
  WalletAccount,
} from '@wallet-standard/core';

import { getChainIdByEnv } from 'modules/api';
import { currentEnv } from 'modules/common';

import { WalletId } from '../const';
import { getFullnodeUrlByEnv } from './getFullnodeUrlByEnv';
import { getIotaWalletById } from './getIotaWalletById';

const ERROR_WALLET_ACCOUNT_NOT_FOUND = new Error('Wallet account not found');
const ERROR_NEW_WALLET_ACCOUNT_NOT_FOUND = new Error(
  'New wallet account not found',
);
const ERROR_WALLET_NOT_CONNECTED = new Error("Wallet isn't connected");

type WalletWithIotaFeatures = WalletWithFeatures<
  IotaSignTransactionFeature &
    StandardConnectFeature &
    StandardDisconnectFeature &
    StandardEventsFeature
>;

export class IotaProvider {
  private static instance: IotaProvider;

  private client: IotaClient;

  private walletAccount: WalletAccount | undefined;

  private wallet: WalletWithIotaFeatures | undefined;

  public isConnected = false;

  public walletId: WalletId | undefined;

  private constructor() {
    this.client = new IotaClient({ url: getFullnodeUrlByEnv(currentEnv) });
  }

  static getInstance(): IotaProvider {
    if (!IotaProvider.instance) {
      IotaProvider.instance = new IotaProvider();
    }

    return IotaProvider.instance;
  }

  public getClient(): IotaClient {
    return this.client;
  }

  public getAccounts(): string[] {
    return this.wallet?.accounts.map(account => account.address) || [];
  }

  public getWalletAccount() {
    if (!this.walletAccount) {
      throw ERROR_WALLET_ACCOUNT_NOT_FOUND;
    }

    return this.walletAccount;
  }

  public getCurrentChainId(): IdentifierString | undefined {
    if (!this.walletAccount) {
      return undefined;
    }

    return this.walletAccount.chains[0];
  }

  public updateWalletAccount(accountAddress: string): void {
    const newWalletAccount = this.wallet?.accounts.find(
      account => account.address.toLowerCase() === accountAddress.toLowerCase(),
    );

    if (!newWalletAccount) {
      throw ERROR_NEW_WALLET_ACCOUNT_NOT_FOUND;
    }

    this.walletAccount = newWalletAccount;
  }

  public getWallet() {
    if (!this.wallet) {
      throw ERROR_WALLET_NOT_CONNECTED;
    }

    return this.wallet;
  }

  public async connect(walletId: string): Promise<void> {
    try {
      const wallet = getIotaWalletById(walletId, getChainIdByEnv(currentEnv));

      this.wallet = wallet as WalletWithIotaFeatures;

      const walletAccount =
        await this.wallet.features['standard:connect'].connect();

      this.walletAccount = walletAccount?.accounts?.[0];

      if (!this.walletAccount) {
        throw new Error('Wallet account not found');
      }

      this.walletId = walletId;

      this.isConnected = true;
    } catch (error) {
      throw new Error(
        `Couldn't connect${this.walletId ? ` to the ${this.walletId}` : ''}.${error instanceof Error ? ` ${error.toString()}` : ''}`,
      );
    }
  }

  public async disconnect(): Promise<void> {
    try {
      await this.wallet?.features['standard:disconnect']?.disconnect();

      this.isConnected = false;
      this.walletAccount = undefined;
      this.walletId = undefined;
      this.wallet = undefined;
    } catch (error) {
      throw new Error(
        `Couldn't disconnect${this.walletId ? ` from the ${this.walletId}` : ''}`,
      );
    }
  }
}
